<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<script setup lang="ts">
import type { ColorbarInfo, ImageCell, Theme } from './report-typing';

import { useImageClipboardStore } from '@src/store/image-clipboard-store';
import { useUpdatedReportStore } from '@src/store/report-store';
import * as fabric from 'fabric';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { colormaps } from '@/src/components/analysis/colormaps';

// ─── Props ───────────────────────────────────────────────────────────────────

const props = defineProps<{
  cell: ImageCell;
  imageHeight: number;
}>();
const COLORBAR_THICKNESS = 10;
const COLORBAR_LENGTH = 140;
const COLORBAR_RIGHT_OFFSET = 8;

function getColorStops(colormapName: string) {
  const colormapFn = colormaps[colormapName];
  const stops: { offset: number; color: string }[] = [];
  const numStops = 10;
  for (let i = 0; i < numStops; i++) {
    const t = i / (numStops - 1);
    stops.push({ offset: t, color: colormapFn(t) });
  }
  return stops;
}

// ─── Store ───────────────────────────────────────────────────────────────────

const reportStore = useUpdatedReportStore();

const theme = computed<Theme | null>(() => {
  const t = reportStore.getTemplate();
  return t?.theme ?? null;
});

const isEditing = computed(() => reportStore.isEditing);

// ─── Refs ────────────────────────────────────────────────────────────────────

const canvasEl = ref<HTMLCanvasElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

let fabricCanvas: fabric.Canvas | null = null;
let fabricImage: fabric.Image | null = null;
let activeColorbarInfo: ColorbarInfo | null = null;
let originalLogicalWidth = 300;
let originalLogicalHeight = props.imageHeight;

const hasImage = ref(false);
const isDragOver = ref(false);
const screenZoom = ref(1);
const mouseInside = ref(false);

const showToolbar = computed(() => mouseInside.value || isDragOver.value);

const showZoomHint = ref(false);
let zoomHintTimeout: any = null;

const isMac
  = typeof window !== 'undefined'
    && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent || navigator.platform || '');
const modifierKeyName = isMac ? 'Cmd' : 'Ctrl';

function triggerZoomHint() {
  showZoomHint.value = true;
  if (zoomHintTimeout) clearTimeout(zoomHintTimeout);
  zoomHintTimeout = setTimeout(() => {
    showZoomHint.value = false;
  }, 1800);
}

// ─── Theme-driven styles ─────────────────────────────────────────────────────

const placeholderStyle = computed(() => {
  if (!theme.value) return {};
  return {
    borderColor: theme.value.imagePlaceholder.borderColor,
    background: theme.value.imagePlaceholder.background,
  };
});

const mutedTextStyle = computed(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.label.family,
    fontSize: `${theme.value.fonts.label.size}px`,
    color: theme.value.colors.muted,
  };
});

/**
 * Draw the colorbar directly on the canvas 2D context.
 * Called from after:render so it works for both screen display AND toDataURL export.
 * The ctx parameter comes from the render event — during toDataURL it is the
 * temporary export canvas context, so the colorbar naturally appears in the
 * exported PNG without any special positioning logic.
 */
function renderColorbarOverlay(ctx: CanvasRenderingContext2D) {
  if (!activeColorbarInfo || !fabricCanvas) return;

  const pixelW = ctx.canvas.width;
  const logicalW = originalLogicalWidth;
  const logicalH = originalLogicalHeight;
  const dpr = pixelW / logicalW; // accounts for retina + toDataURL multiplier

  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in logical-pixel coords

  const barW = COLORBAR_THICKNESS;
  const textH = 10; // font size is bold 10px Arial
  const pad = 6;
  const gap = 4;
  const minPaddingTopBottom = 8;
  const maxAvailableH
    = logicalH
      - (minPaddingTopBottom * 2 + pad + textH + gap + gap + textH + pad);
  const barH = Math.max(40, Math.min(COLORBAR_LENGTH, maxAvailableH));
  const totalH = pad + textH + gap + barH + gap + textH + pad;
  const bgW = barW + 25;
  const bgX = logicalW - bgW - COLORBAR_RIGHT_OFFSET;
  const bgY = (logicalH - totalH) / 2;

  // ── Background rounded rect ──
  const r = 4;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.beginPath();
  ctx.moveTo(bgX + r, bgY);
  ctx.lineTo(bgX + bgW - r, bgY);
  ctx.arcTo(bgX + bgW, bgY, bgX + bgW, bgY + r, r);
  ctx.lineTo(bgX + bgW, bgY + totalH - r);
  ctx.arcTo(bgX + bgW, bgY + totalH, bgX + bgW - r, bgY + totalH, r);
  ctx.lineTo(bgX + r, bgY + totalH);
  ctx.arcTo(bgX, bgY + totalH, bgX, bgY + totalH - r, r);
  ctx.lineTo(bgX, bgY + r);
  ctx.arcTo(bgX, bgY, bgX + r, bgY, r);
  ctx.closePath();
  ctx.fill();

  // ── Gradient bar ──
  const barX = bgX + (bgW - barW) / 2;
  const topTextY = bgY + pad;
  const barY = topTextY + textH + gap;
  const bottomTextY = barY + barH + gap;

  const stops = getColorStops(activeColorbarInfo.colormap);
  const grad = ctx.createLinearGradient(barX, barY + barH, barX, barY);
  stops.forEach(s => grad.addColorStop(s.offset, s.color));

  ctx.fillStyle = grad;
  ctx.fillRect(barX, barY, barW, barH);

  // ── Bar border ──

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';

  ctx.lineWidth = 1;
  ctx.strokeRect(barX, barY, barW, barH);

  // ── Labels ──

  ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';

  ctx.shadowBlur = 2;

  ctx.shadowOffsetX = 1;

  ctx.shadowOffsetY = 1;

  ctx.fillStyle = '#ffffff';
  // make text bold:

  ctx.font = 'bold 10px Arial';

  ctx.textAlign = 'center';

  ctx.textBaseline = 'top';
  ctx.fillText(activeColorbarInfo.maxValue.toFixed(2), bgX + bgW / 2, topTextY);

  ctx.fillText(
    activeColorbarInfo.minValue.toFixed(2),
    bgX + bgW / 2,
    bottomTextY,
  );

  ctx.restore();
}

// ─── Export to store ─────────────────────────────────────────────────────────

function exportToStore() {
  if (!fabricCanvas) return;

  // The colorbar is drawn via after:render directly on the rendering context,
  // so it naturally appears in toDataURL output — no special handling needed.
  const dataUrl = fabricCanvas.toDataURL({
    format: 'png',
    quality: 1,
    multiplier: 2, // 2x for sharper PDF output
  });

  reportStore.setValue(props.cell.imageField.key, dataUrl);

  // Save current viewport transform
  const vpt = fabricCanvas.viewportTransform;
  reportStore.setValue(
    `${props.cell.imageField.key}_vpt`,
    vpt ? JSON.stringify(vpt) : null,
  );
}

function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: any = null;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

const debouncedExportToStore = debounce(exportToStore, 300);

// ─── Image loading ───────────────────────────────────────────────────────────

async function loadImageOntoCanvas(
  dataUrl: string,
  vptToRestore?: fabric.TMat2D | null,
  colorbarInfoToRestore?: ColorbarInfo | null,
) {
  if (!fabricCanvas) return;

  // Ensure canvas dimensions match latest container width before calculating scale/center
  const container = canvasEl.value?.closest('.image-cell-root');
  const currentW = container?.clientWidth || fabricCanvas.getWidth();
  if (currentW > 0 && currentW !== fabricCanvas.getWidth()) {
    fabricCanvas.setDimensions({
      width: currentW,
      height: props.imageHeight,
    });
    originalLogicalWidth = currentW;
  }

  // Remove existing image
  if (fabricImage) {
    fabricCanvas.remove(fabricImage);
    fabricImage = null;
  }

  // Reset viewport
  fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

  try {
    const img = await fabric.FabricImage.fromURL(dataUrl, { crossOrigin: 'anonymous' });
    if (!fabricCanvas) return;

    const cw = fabricCanvas.getWidth();
    const ch = fabricCanvas.getHeight();

    // Scale to fit canvas
    const scaleX = cw / img.width;
    const scaleY = ch / img.height;
    const scale = Math.min(scaleX, scaleY);

    img.set({
      originX: 'center',
      originY: 'center',
      left: cw / 2,
      top: ch / 2,
      scaleX: scale,
      scaleY: scale,
      hasControls: false,
      hasBorders: false,
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
      centeredScaling: true,
      cornerSize: 8,
      cornerColor: '#0284c7',
      transparentCorners: false,
    });

    // Hide rotation control
    img.setControlsVisibility({ mtr: false });

    fabricImage = img;
    fabricCanvas.add(img);
    fabricCanvas.setActiveObject(img);

    // Restore states if available
    if (vptToRestore) {
      fabricCanvas.setViewportTransform(vptToRestore);

      screenZoom.value = vptToRestore[0];
    }
    else {
      screenZoom.value = 1;
    }

    // Set active colorbar (will be drawn in after:render)
    activeColorbarInfo = colorbarInfoToRestore || null;

    fabricCanvas.renderAll();

    hasImage.value = true;
    exportToStore();
  }
  catch (err) {
    console.error('Failed to load image onto canvas:', err);
  }
}

function restoreFromStore() {
  const rawImage = reportStore.getValue(
    `${props.cell.imageField.key}_rawImage`,
  );
  const colorbarInfo = reportStore.getValue(
    `${props.cell.imageField.key}_colorbarInfo`,
  );
  const vptStr = reportStore.getValue(`${props.cell.imageField.key}_vpt`);

  if (rawImage && typeof rawImage === 'string' && rawImage.length > 10) {
    let vpt: fabric.TMat2D | null = null;
    if (vptStr && typeof vptStr === 'string') {
      try {
        vpt = JSON.parse(vptStr);
      }
      catch (e) {
        console.warn('Failed to parse vpt:', e);
      }
    }
    loadImageOntoCanvas(rawImage, vpt, colorbarInfo as ColorbarInfo | null);
  }
  else {
    const stored = reportStore.getValue(props.cell.imageField.key);
    if (stored && typeof stored === 'string' && stored.length > 10) {
      loadImageOntoCanvas(stored);
    }
  }
}

// ─── File handling ───────────────────────────────────────────────────────────

const ACCEPTED_MIME = new Set(['image/jpeg', 'image/png', 'image/svg+xml']);
const ACCEPTED_EXT = ['.jpg', '.jpeg', '.png', '.svg'];

function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_MIME.has(file.type)) return true;
  const name = (file.name || '').toLowerCase();
  return ACCEPTED_EXT.some(ext => name.endsWith(ext));
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function handleFile(file: File) {
  if (!isAcceptedFile(file)) {
    console.warn('Rejected file:', file.name, file.type);
    return;
  }
  const dataUrl = await fileToDataUrl(file);
  reportStore.setValue(`${props.cell.imageField.key}_rawImage`, dataUrl);
  reportStore.setValue(`${props.cell.imageField.key}_colorbarInfo`, null);
  loadImageOntoCanvas(dataUrl);
}

// ─── Drag & drop ─────────────────────────────────────────────────────────────

function onDragEnter() {
  isDragOver.value = true;
}
function onDragOver() {
  isDragOver.value = true;
}
function onDragLeave() {
  isDragOver.value = false;
}

async function onDrop(event: DragEvent) {
  isDragOver.value = false;
  if (!isEditing.value) return;

  const dt = event.dataTransfer;
  if (!dt) return;

  // Try application/json for clipboard image first
  const clipboardData = dt.getData('application/json');
  if (clipboardData) {
    try {
      const parsed = JSON.parse(clipboardData);
      if (parsed && parsed.type === 'clipboard-image' && parsed.screenshotUID) {
        const clipboardStore = useImageClipboardStore();
        const img = clipboardStore.getImageById(parsed.screenshotUID);
        if (img) {
          reportStore.setValue(
            `${props.cell.imageField.key}_rawImage`,
            img.imageDataURL,
          );
          reportStore.setValue(
            `${props.cell.imageField.key}_colorbarInfo`,
            img.colorbarInfo || null,
          );
          if (img.title) {
            reportStore.setValue(props.cell.titleField.key, img.title);
          }
          loadImageOntoCanvas(img.imageDataURL, null, img.colorbarInfo);
          return;
        }
      }
    }
    catch (err) {
      console.warn('Failed to parse clipboard drag data:', err);
    }
  }

  // Try file next
  let file: File | null = null;
  if (dt.items?.length) {
    const fileItem = Array.from(dt.items).find(item => item.kind === 'file');
    file = fileItem?.getAsFile() ?? null;
  }
  if (!file && dt.files?.length) {
    [file] = dt.files;
  }

  if (file) {
    await handleFile(file);
    return;
  }

  // Fallback: image URL from browser drag
  const types = dt.types || [];
  if (types.includes('text/uri-list') || types.includes('text/html')) {
    const imageUrl = dt.getData('text/uri-list')?.split('\n')?.[0]?.trim();
    const htmlData = dt.getData('text/html');
    const srcMatch = htmlData?.match(/<img[^>]+src="([^"]+)"/i);
    const fallbackUrl = srcMatch?.[1];
    const url = imageUrl || fallbackUrl;

    if (url) {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const f = new File([blob], 'dropped-image.png', { type: blob.type });
        await handleFile(f);
      }
      catch (err) {
        console.warn('Failed to fetch dropped image URL:', err);
      }
    }
  }
}

// ─── Upload button ───────────────────────────────────────────────────────────

function openFilePicker() {
  fileInput.value?.click();
}

async function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) await handleFile(file);
  // Reset input so re-selecting the same file triggers change
  target.value = '';
}

// ─── Actions ─────────────────────────────────────────────────────────────────

function resetView() {
  if (!fabricCanvas || !fabricImage) return;

  // Reset viewport transform
  fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
  screenZoom.value = 1;

  const cw = fabricCanvas.getWidth();
  const ch = fabricCanvas.getHeight();
  const scaleX = cw / fabricImage.width;
  const scaleY = ch / fabricImage.height;
  const scale = Math.min(scaleX, scaleY);

  fabricImage.set({
    originX: 'center',
    originY: 'center',
    left: cw / 2,
    top: ch / 2,
    scaleX: scale,
    scaleY: scale,
    angle: 0,
  });
  fabricImage.setCoords();
  fabricCanvas.renderAll();
  exportToStore();
}

function clearImage() {
  if (!fabricCanvas) return;

  if (fabricImage) {
    fabricCanvas.remove(fabricImage);
    fabricImage = null;
  }
  activeColorbarInfo = null;
  fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
  screenZoom.value = 1;
  fabricCanvas.renderAll();

  hasImage.value = false;
  reportStore.setValue(props.cell.imageField.key, null);
  reportStore.setValue(`${props.cell.imageField.key}_rawImage`, null);
  reportStore.setValue(`${props.cell.imageField.key}_colorbarInfo`, null);
  reportStore.setValue(`${props.cell.imageField.key}_vpt`, null);
  reportStore.setValue(props.cell.titleField.key, '');
}

// ─── Lock / unlock on editing toggle ─────────────────────────────────────────

watch(isEditing, (editing) => {
  if (!fabricCanvas) return;

  if (!editing) {
    // Export final snapshot before locking
    exportToStore();
    // Deselect & disable interaction
    fabricCanvas.discardActiveObject();
    fabricCanvas.forEachObject((obj: fabric.FabricObject) => {
      obj.selectable = false;
      obj.evented = false;
    });
    fabricCanvas.renderAll();
  }
  else {
    // Re-enable interaction
    fabricCanvas.forEachObject((obj: fabric.FabricObject) => {
      obj.selectable = true;
      obj.evented = true;
    });
    fabricCanvas.renderAll();
  }
});

// ─── Canvas setup ────────────────────────────────────────────────────────────

function initCanvas() {
  if (!canvasEl.value) return;

  const container = canvasEl.value.parentElement!;
  const w = container.clientWidth;
  const h = props.imageHeight;

  originalLogicalWidth = w;
  originalLogicalHeight = h;

  fabricCanvas = new fabric.Canvas(canvasEl.value, {
    width: w,
    height: h,
    selection: false,
    backgroundColor: 'transparent',
    allowTouchScrolling: false,
    preserveObjectStacking: true,
  });

  // Mouse-wheel zoom
  fabricCanvas.on('mouse:wheel', (opt: any) => {
    if (!isEditing.value) return;
    const evt = opt.e as WheelEvent;

    // Only zoom if Ctrl or Cmd key is held down to prevent page scroll conflict
    if (!evt.ctrlKey && !evt.metaKey) {
      triggerZoomHint();
      return;
    }

    evt.preventDefault();
    evt.stopPropagation();

    const delta = evt.deltaY;
    let zoom = fabricCanvas!.getZoom();
    zoom *= 0.999 ** delta;
    zoom = Math.max(0.1, Math.min(10, zoom));

    fabricCanvas!.zoomToPoint(new fabric.Point(evt.offsetX, evt.offsetY), zoom);
    screenZoom.value = zoom;
    fabricCanvas!.requestRenderAll();
    debouncedExportToStore();
  });

  // Pan via alt+drag on canvas background
  let isPanning = false;
  let lastPanX = 0;
  let lastPanY = 0;

  fabricCanvas.on('mouse:down', (opt: any) => {
    if (!isEditing.value) return;
    const evt = opt.e as MouseEvent;
    if (evt.altKey || (!fabricImage && false)) {
      isPanning = true;
      lastPanX = evt.clientX;
      lastPanY = evt.clientY;
      fabricCanvas!.setCursor('grab');
    }
  });

  fabricCanvas.on('mouse:move', (opt: any) => {
    if (!isPanning) return;
    const evt = opt.e as MouseEvent;
    const vpt = fabricCanvas!.viewportTransform!;
    vpt[4] += evt.clientX - lastPanX;
    vpt[5] += evt.clientY - lastPanY;
    lastPanX = evt.clientX;
    lastPanY = evt.clientY;
    fabricCanvas!.requestRenderAll();
  });

  fabricCanvas.on('mouse:up', () => {
    if (isPanning) {
      isPanning = false;
      fabricCanvas!.setCursor('default');
      exportToStore();
    }
  });

  // After any image manipulation, re-export
  fabricCanvas.on('object:modified', () => {
    exportToStore();
  });

  // Draw colorbar overlay after every render (works for both screen and toDataURL)
  fabricCanvas.on('after:render', (opt: any) => {
    const ctx = opt?.ctx || fabricCanvas!.contextContainer;
    renderColorbarOverlay(ctx);
  });

  // Mouse enter/leave for toolbar is now handled directly via Vue directives on the root element
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  initCanvas();
  restoreFromStore();

  const container = canvasEl.value?.closest('.image-cell-root') || canvasEl.value?.parentElement;
  if (container) {
    resizeObserver = new ResizeObserver((entries) => {
      if (!fabricCanvas || entries.length === 0) return;
      const { width } = entries[0].contentRect;
      if (width > 0 && Math.abs(width - fabricCanvas.getWidth()) > 1) {
        originalLogicalWidth = width;
        originalLogicalHeight = props.imageHeight;
        fabricCanvas.setDimensions({
          width,
          height: props.imageHeight,
        });

        if (fabricImage) {
          const cw = width;
          const ch = props.imageHeight;
          const scaleX = cw / fabricImage.width;
          const scaleY = ch / fabricImage.height;
          const scale = Math.min(scaleX, scaleY);

          const vpt = fabricCanvas.viewportTransform;
          const isDefaultVpt = !vpt || (vpt[0] === 1 && vpt[3] === 1 && vpt[4] === 0 && vpt[5] === 0);
          if (isDefaultVpt) {
            fabricImage.set({
              originX: 'center',
              originY: 'center',
              left: cw / 2,
              top: ch / 2,
              scaleX: scale,
              scaleY: scale,
            });
            fabricImage.setCoords();
          }
        }
        fabricCanvas.renderAll();
      }
    });
    resizeObserver.observe(container);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (fabricCanvas) {
    fabricCanvas.dispose();
    fabricCanvas = null;
  }
  if (zoomHintTimeout) {
    clearTimeout(zoomHintTimeout);
  }
});
</script>

<template>
  <div
    class="image-cell-root"
    :style="{ height: `${imageHeight}px` }"
    :class="{ 'is-hovering': isDragOver, 'is-locked': !isEditing, 'has-image': hasImage }"
    @dragenter.prevent.stop="onDragEnter"
    @dragover.prevent.stop="onDragOver"
    @dragleave.prevent.stop="onDragLeave"
    @drop.prevent.stop="onDrop"
    @mouseenter="mouseInside = true"
    @mouseleave="mouseInside = false"
  >
    <!-- Fabric canvas -->
    <canvas ref="canvasEl" />

    <!-- Empty-state overlay (shown when no image loaded) -->
    <div
      v-if="!hasImage && isEditing"
      class="empty-overlay"
      :style="placeholderStyle"
    >
      <i
        class="fas fa-image fa-2x"
        style="color: #94a3b8; margin-bottom: 4px"
      />
      <span :style="mutedTextStyle">
        {{ cell.imageField.placeholder || 'Drop image here' }}
      </span>
    </div>

    <!-- Zoom hint overlay -->
    <Transition name="hint-fade">
      <div v-if="showZoomHint && hasImage" class="zoom-hint-overlay">
        <div class="zoom-hint-badge">
          <kbd class="zoom-hint-key">{{ modifierKeyName }}</kbd>
          <span>+</span>
          <i class="fas fa-mouse" style="margin: 0 2px" />
          <span>Scroll to zoom</span>
        </div>
      </div>
    </Transition>

    <!-- Toolbar (visible on hover when editing and image loaded) -->
    <Transition name="toolbar-fade">
      <div v-if="isEditing && showToolbar" class="cell-toolbar">
        <button class="tb-btn" title="Upload Image" @click="openFilePicker">
          <i class="fas fa-upload" style="color: #fff" />
        </button>
        <button
          v-if="hasImage"
          class="tb-btn"
          title="Reset View"
          @click="resetView"
        >
          <i class="fas fa-arrows-to-circle" style="color: #fff" />
        </button>
        <button
          v-if="hasImage"
          class="tb-btn tb-btn-danger"
          title="Clear Image"
          @click="clearImage"
        >
          <i class="fas fa-trash" style="color: #fff" />
        </button>
      </div>
    </Transition>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/svg+xml"
      style="display: none"
      @change="onFileSelected"
    >
  </div>
</template>

<style scoped>
.image-cell-root {
  position: relative;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  border: 2px dashed #cbd5e1;
  background: #f8fafc;
  border-radius: 4px;
  transition:
    border-color 0.2s,
    background 0.2s;
}

.image-cell-root.has-image {
  border: none;
  background: transparent;
}

.image-cell-root:hover {
  border-color: #94a3b8;
}

.image-cell-root.is-hovering {
  border-color: #0284c7;
  background: #e0f2fe;
}

.image-cell-root.is-locked {
  border-style: solid;
  border-color: #e2e8f0;
  pointer-events: none;
}

/* Fabric wraps the canvas in a container div — stretch it */
.image-cell-root :deep(.canvas-container) {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

.image-cell-root :deep(canvas) {
  max-width: 100%;
  box-sizing: border-box;
}

.empty-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  border-style: none; /* border is on root */
}

/* ── Toolbar ── */
.cell-toolbar {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 4px;
  z-index: 10;
}

.tb-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: rgba(58, 57, 57, 0.45);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.tb-btn:hover {
  background: #0284c7cc;
}

.tb-btn-danger:hover {
  background: #dc2626cc;
}

/* Toolbar fade transition */
.toolbar-fade-enter-active,
.toolbar-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toolbar-fade-enter-from,
.toolbar-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Zoom Hint Overlay ── */
.zoom-hint-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 20;
}

.zoom-hint-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
}

.zoom-hint-key {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 1px 6px;
  font-family: inherit;
  font-size: 11px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
}

/* Hint fade transition */
.hint-fade-enter-active,
.hint-fade-leave-active {
  transition:
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hint-fade-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(5px);
}

.hint-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-5px);
}
</style>
